import React, { useMemo, useState, useEffect } from 'react';
import { NavLink, useLocation, matchPath } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useMsal } from '@azure/msal-react';
import apiClient, { getUserCompanyIds, isUserAdmin } from '../../services/apiClient';
import { resolveRole } from '../../utils/roles';
import {
  HomeIcon,
  UserGroupIcon,
  CogIcon,
  DocumentTextIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ChartBarIcon,
  BuildingOfficeIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ListBulletIcon,
  UsersIcon,
  TableCellsIcon
} from '@heroicons/react/24/outline';

const CustomerNavGroup = ({
  customer,
  currentCustomerId,
  currentPhaseId,
  pathname,
  forceExpanded = false
}: {
  customer: any;
  currentCustomerId?: string;
  currentPhaseId?: string;
  pathname: string;
  forceExpanded?: boolean;
}) => {
  const isSelected = currentCustomerId === customer.id;
  const [isExpanded, setIsExpanded] = useState(isSelected || forceExpanded);

  // Auto-expand if this customer becomes selected or if forced
  // Auto-collapse if this customer is NOT selected (unless forced)
  useEffect(() => {
    if (forceExpanded) {
      setIsExpanded(true);
    } else if (isSelected) {
      setIsExpanded(true);
    } else {
      setIsExpanded(false);
    }
  }, [isSelected, forceExpanded, pathname]);

  const { data: phases = [] } = useQuery({
    queryKey: ['phases', customer.id],
    queryFn: async () => {
      try {
        return await apiClient.getPhases(customer.id);
      } catch (error) {
        return [];
      }
    },
    enabled: isExpanded,
    staleTime: 5 * 60 * 1000,
  });

  return (
    <div className="mb-px">
      <div className={`flex items-center rounded-md transition-colors ${isSelected ? 'bg-gray-800 text-white' : 'text-gray-400 hover:bg-gray-800 hover:text-white'
        }`}>
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!forceExpanded) {
              setIsExpanded(!isExpanded);
            }
          }}
          className={`p-2 rounded-l-md hover:bg-white/5 ${isSelected ? 'text-brand-red' : 'text-gray-500 hover:text-gray-300'
            } ${forceExpanded ? 'opacity-50 cursor-default hover:bg-transparent' : ''}`}
          disabled={forceExpanded}
        >
          {isExpanded ? (
            <ChevronDownIcon className="w-3 h-3" />
          ) : (
            <ChevronRightIcon className="w-3 h-3" />
          )}
        </button>
        <NavLink
          to={`/customers/${customer.id}/phases`}
          className="flex-1 py-1 pr-3 text-sm font-medium truncate flex items-center"
        >
          <BuildingOfficeIcon
            className={`w-4 h-4 mr-3 flex-shrink-0 ${isSelected ? 'text-brand-red' : 'text-gray-500'
              }`}
          />
          <span className="truncate">{customer.name}</span>
        </NavLink>
      </div>

      {isExpanded && (
        <div className="ml-4 border-l border-gray-700 mt-1 mb-2 space-y-1">
          {phases.map((phase: any) => {
            const isPhaseActive = currentPhaseId === phase.id;
            return (
              <NavLink
                key={phase.id}
                to={`/customers/${customer.id}/phases/${phase.id}`}
                className={({ isActive }) => `
                  flex items-center pl-4 pr-2 py-1.5 text-xs rounded-r-md transition-colors border-l-2 -ml-px
                  ${isPhaseActive || isActive
                    ? 'border-brand-red text-white bg-gray-800/50 font-medium'
                    : 'border-transparent text-gray-500 hover:text-gray-300 hover:bg-gray-800'
                  }
                `}
              >
                <span className="truncate">{phase.name}</span>
              </NavLink>
            );
          })}
          {phases.length === 0 && (
            <div className="pl-6 py-1.5 text-xs text-gray-600 italic">No phases found</div>
          )}
        </div>
      )}
    </div>
  );
};

const Navigation: React.FC = () => {
  const location = useLocation();
  const { instance } = useMsal();

  // Extract IDs from URL since useParams() doesn't work in this context (outside Routes)
  const customerMatch = matchPath({ path: '/customers/:customerId/*' }, location.pathname);
  const phaseMatch = matchPath({ path: '/customers/:customerId/phases/:phaseId/*' }, location.pathname);

  const customerId = customerMatch?.params.customerId;
  const phaseId = phaseMatch?.params.phaseId;

  // Get user roles and permissions
  const isAdmin = useMemo(() => isUserAdmin(), [instance]);
  const userCompanyIds = useMemo(() => getUserCompanyIds(), [instance]);

  // Resolve specific role
  const role = useMemo(() => resolveRole(instance), [instance]);

  const isCustomerRole = role === 'customer';

  // Fetch customers
  const { data: customers = [] } = useQuery({
    queryKey: ['customers'],
    queryFn: async () => {
      try {
        return await apiClient.getCustomers();
      } catch (error) {
        console.error('Failed to fetch customers for navigation:', error);
        return [];
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Filter available customers based on role
  const availableCustomers = useMemo(() => {
    if (!customers) return [];

    // Filter out archived customers first
    const activeCustomers = customers.filter((c: any) => !c.archived);

    // Admin sees all active customers
    if (isAdmin) {
      return activeCustomers;
    }

    // Business users see only their assigned customers
    return activeCustomers.filter((c: any) => userCompanyIds.includes(c.id));
  }, [customers, isAdmin, userCompanyIds]);

  const navItems: { to: string; icon: React.ElementType; label: string; end?: boolean }[] = [
    { to: '/dashboard', icon: HomeIcon, label: 'Dashboard' },
  ];

  // Show All Customers and Reports only for non-customer roles (Admin/Consultant)
  if (!isCustomerRole) {
    navItems.push(
      { to: '/customers', icon: UserGroupIcon, label: 'All Customers', end: true },
      // { to: '/reports', icon: ChartBarIcon, label: 'Reports' }
    );
  }

  if (isAdmin) {
    navItems.push({ to: '/admin/users', icon: UsersIcon, label: 'Manage Users' });
  }



  const phaseNavItems = customerId && phaseId ? [
    { to: `/customers/${customerId}/phases/${phaseId}/wizard`, icon: ListBulletIcon, label: 'Wizard' },
    { to: `/customers/${customerId}/phases/${phaseId}/credentials`, icon: CogIcon, label: 'Credentials' },
    { to: `/customers/${customerId}/phases/${phaseId}/entities`, icon: ListBulletIcon, label: 'Entities' },
    { to: `/customers/${customerId}/phases/${phaseId}/metadata`, icon: DocumentTextIcon, label: 'Metadata' },
    { to: `/customers/${customerId}/phases/${phaseId}/files`, icon: DocumentTextIcon, label: 'Files' },
    { to: `/customers/${customerId}/phases/${phaseId}/mapping-v3`, icon: ListBulletIcon, label: 'Field Mapping' },
    { to: `/customers/${customerId}/phases/${phaseId}/validation`, icon: CheckCircleIcon, label: 'Validation' },
    { to: `/customers/${customerId}/phases/${phaseId}/export`, icon: ArrowPathIcon, label: 'Export' },
  ] : [];

  return (
    <nav className="w-64 bg-brand-navy shadow-soft border-r border-gray-800 flex flex-col h-full overflow-y-auto text-gray-300">
      <div className="p-4">
        {/* Main Navigation */}
        <div className="space-y-1 mb-6">
          {navItems.map(({ to, icon: Icon, label, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${isActive
                  ? 'bg-brand-red text-white shadow-md'
                  : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                }`
              }
            >
              <Icon className="w-5 h-5 mr-3" />
              {label}
            </NavLink>
          ))}
        </div>

        {/* Available Customers Section */}
        {availableCustomers.length > 0 && (
          <div className="mb-6">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 px-3">
              Active Customers
            </h3>
            <div className="space-y-1">
              {availableCustomers.map((customer: any) => (
                <CustomerNavGroup
                  key={customer.id}
                  customer={customer}
                  currentCustomerId={customerId}
                  currentPhaseId={phaseId}
                  pathname={location.pathname}
                  forceExpanded={isCustomerRole}
                />
              ))}
            </div>
          </div>
        )}

        {/* Phase Operations */}
        {phaseNavItems.length > 0 && (
          <div className="pt-6 border-t border-gray-800 pb-10">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 px-3">
              Phase Tools
            </h3>
            <div className="space-y-1">
              {phaseNavItems.map(({ to, icon: Icon, label }) => (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) =>
                    `flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${isActive
                      ? 'text-brand-red bg-gray-800/50'
                      : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                    }`
                  }
                >
                  <Icon className="w-5 h-5 mr-3" />
                  {label}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;